import { PageHero } from "@/components/site/PageHero";
import { Reveal, Overline } from "@/components/site/Reveal";
import { CTABand } from "@/components/site/CTABand";
import { sectors, IMAGES } from "@/data/content";
import { Check } from "lucide-react";

const highlights = [
  { title: "Commercial Properties", body: "Offices, business parks and mixed-use developments rely on TFS for security, cleaning, maintenance and front-of-house that reflect their brand.", image: IMAGES.commercial, points: ["Corporate reception & concierge", "Daily cleaning & washrooms", "PPM & reactive maintenance"] },
  { title: "Residential Developments", body: "Build-to-rent operators and residential blocks trust us to keep communal areas safe, pristine and welcoming for residents.", image: IMAGES.residential, points: ["Estate security & patrols", "Communal cleaning", "Parking enforcement"] },
  { title: "Construction Sites", body: "Contractors depend on our site security, welfare cleaning, waste clearance and fire safety cover throughout the build programme.", image: IMAGES.construction, points: ["Site security & keyholding", "Waste & bulky clearance", "Fire wardens & waking watch"] },
];

export default function Sectors() {
  return (
    <div>
      <PageHero
        overline="Sectors We Support"
        title={["The right service", "for every kind", "of estate."]}
        subtitle="From single commercial buildings to national residential portfolios and live construction sites, our teams adapt to the demands of your sector."
        image={IMAGES.skyline}
      />

      <section className="bg-ink py-20 md:py-28 border-t border-border">
        <div className="max-w-container mx-auto px-6 md:px-12">
          <Reveal><Overline>Who we work with</Overline></Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {sectors.map((sec, i) => {
              const Icon = sec.icon;
              return (
                <Reveal key={sec.title} delay={(i % 4) * 0.05}>
                  <div className="bg-surface border border-border p-7 h-full hover:border-brand/50 transition-colors duration-300">
                    <Icon size={30} className="text-brand-light" strokeWidth={1.5} />
                    <h3 className="mt-5 font-display font-medium text-xl text-white tracking-tight">{sec.title}</h3>
                    <p className="mt-2 text-sm text-slatetxt leading-relaxed">{sec.blurb}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-surface py-16 md:py-24">
        <div className="max-w-container mx-auto px-6 md:px-12 space-y-4 md:space-y-6">
          {highlights.map((h, i) => {
            const reverse = i % 2 === 1;
            return (
              <Reveal key={h.title}>
                <div className="grid grid-cols-1 lg:grid-cols-12 border border-border bg-ink overflow-hidden">
                  <div className={`lg:col-span-6 relative min-h-[300px] ${reverse ? "lg:order-2" : ""}`}>
                    <img src={h.image} alt={h.title} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className={`lg:col-span-6 p-8 md:p-12 flex flex-col justify-center ${reverse ? "lg:order-1" : ""}`}>
                    <h2 className="font-display font-bold text-3xl md:text-4xl text-white tracking-tight">{h.title}</h2>
                    <p className="mt-4 text-slatetxt text-lg leading-relaxed">{h.body}</p>
                    <ul className="mt-6 space-y-3">
                      {h.points.map((p) => (
                        <li key={p} className="flex items-center gap-3 text-white/85">
                          <Check size={18} className="text-brand-light shrink-0" /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
